import React, { useEffect, useRef, useState } from 'react'

export interface DropdownOption {
  id: number | string
  name: string
  description?: string
}

interface DropdownInputProps {
  // 필수 표시(빨간 *) 같은 JSX 를 넣을 수 있도록 ReactNode 허용
  label: React.ReactNode
  value: string
  onChange: (value: string) => void
  options: DropdownOption[]
  placeholder?: string
  disabled?: boolean
  onPickExisting: (opt: DropdownOption) => void
  onDirectInput?: () => void
  maxDescriptionChars?: number
  inputDisabled?: boolean
  dropdownDisabled?: boolean
}

export default function DropdownInput({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  onPickExisting,
  onDirectInput,
  maxDescriptionChars = 60,
  inputDisabled,
  dropdownDisabled
}: DropdownInputProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return
      if (!containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const isInputDisabled = (typeof inputDisabled === 'boolean') ? inputDisabled : !!disabled
  const isDropdownDisabled = (typeof dropdownDisabled === 'boolean') ? dropdownDisabled : false

  const inputClass = isInputDisabled
    ? 'flex-1 z-10 px-3 py-2 border border-gray-200 rounded-l-md bg-gray-50 text-gray-600 opacity-60 cursor-not-allowed focus:outline-none focus:ring-0'
    : 'flex-1 z-10 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent'

  return (
    <div ref={containerRef} className="w-full">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="relative flex items-stretch">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={isInputDisabled}
          aria-disabled={isInputDisabled}
          className={inputClass}
        />
        <button
          type="button"
          disabled={isDropdownDisabled}
          onClick={() => setOpen(o => !o)}
          className="px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-white hover:bg-gray-50 disabled:opacity-50"
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <svg className="w-4 h-4 text-gray-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
          </svg>
        </button>
        {open && (
          <div className="absolute left-0 right-0 top-full z-50 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-auto">
            {onDirectInput && (
              <>
            <button
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-gray-50"
              onClick={() => {
                setOpen(false)
                onDirectInput()
              }}
            >
              직접입력
            </button>
            <div className="my-1 border-t border-gray-100" />
              </>
            )}
            {options.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-500">항목이 없습니다</div>
            )}
            {options.map(opt => (
              <button
                key={String(opt.id)}
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-gray-50"
                onClick={() => {
                  setOpen(false)
                  onPickExisting(opt)
                }}
              >
                <div className="text-sm text-gray-900">{opt.name}</div>
                {opt.description ? (
                  <div className="text-xs text-gray-500 truncate" title={opt.description}>
                    {opt.description.length > maxDescriptionChars ? `${opt.description.slice(0, maxDescriptionChars)}…` : opt.description}
                  </div>
                ) : null}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}


